import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type { HistoryQuery } from '@/services/loanEmailHistoryService';
import { useHistoryPage } from './useHistoryPage';
import { HistoryRow } from './HistoryRow';
import { HistoryPager } from './HistoryPager';
import { HistoryDetail } from './HistoryDetail';

function HistoryResults({ query }: { query: HistoryQuery }) {
  const state = useHistoryPage(query);
  const [selected, setSelected] = useState<string | null>(null);
  const opener = useRef<HTMLButtonElement | null>(null);
  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <p className='text-xs text-muted-foreground'>Refresh to load new activity from other sessions.</p>
        <Button
          variant='outline'
          size='sm'
          disabled={state.loading}
          onClick={() => {
            setSelected(null);
            state.refresh();
          }}
        >
          Refresh
        </Button>
      </div>
      {state.loading && <p role='status' className='py-8 text-center text-sm'>Loading recorded emails…</p>}
      {state.error && (
        <p role='alert' className='rounded-md border border-destructive/30 p-3 text-sm'>{state.error}</p>
      )}
      {state.data && !state.data.items.length && (
        <p className='py-8 text-center text-sm text-muted-foreground'>
          No recorded emails match this loan and these filters.
        </p>
      )}
      {state.data && (
        <ul aria-label='Recorded loan emails'>
          {state.data.items.map((item) => (
            <HistoryRow
              key={item.id}
              item={item}
              onDetails={(button) => {
                opener.current = button;
                setSelected(item.id);
              }}
            >
              {selected === item.id && (
                <HistoryDetail
                  key={selected}
                  loanId={query.loanId}
                  messageId={selected}
                  onClose={() => {
                    setSelected(null);
                    opener.current?.focus();
                  }}
                />
              )}
            </HistoryRow>
          ))}
        </ul>
      )}
      {state.data && <HistoryPager state={state} label='Email history pages' />}
    </div>
  );
}

function HistoryWorkspace({ loanId }: { loanId: string }) {
  const [search, setSearch] = useState('');
  const [days, setDays] = useState('30');
  const [query, setQuery] = useState<HistoryQuery>({
    action: 'list',
    loanId,
    from: new Date(Date.now() - 30 * 86400000).toISOString()
  });
  return (
    <section aria-label='Loan email history' className='mx-auto max-w-6xl space-y-4'>
      <div>
        <h2 className='font-heading text-xl'>Email history</h2>
        <p className='mt-1 text-sm text-muted-foreground'>
          Recorded emails explicitly linked to this loan. Earlier unlinked messages and texts are not
          included.
        </p>
      </div>
      <form
        className='grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_10rem_auto]'
        onSubmit={(event) => {
          event.preventDefault();
          setQuery({
            action: 'list',
            loanId,
            query: search.trim(),
            from: days === 'all' ? null : new Date(Date.now() - Number(days) * 86400000).toISOString()
          });
        }}
      >
        <div className='min-w-0 space-y-1'>
          <Label htmlFor='loan-email-search'>Subject or recipient</Label>
          <Input
            id='loan-email-search'
            value={search}
            maxLength={200}
            onChange={(event) => setSearch(event.target.value)}
            placeholder='Search recorded emails'
          />
        </div>
        <div className='space-y-1'>
          <Label htmlFor='loan-email-period'>Time period</Label>
          <select
            id='loan-email-period'
            className='h-10 w-full rounded-md border bg-background px-3 text-sm'
            value={days}
            onChange={(event) => setDays(event.target.value)}
          >
            <option value='30'>Last 30 days</option>
            <option value='90'>Last 90 days</option>
            <option value='all'>All recorded</option>
          </select>
        </div>
        <Button type='submit'>Search</Button>
      </form>
      <HistoryResults key={JSON.stringify(query)} query={query} />
    </section>
  );
}

export function LoanEmailHistory({ loanId }: { loanId: string }) {
  const flag = useFeatureFlag('loan_email_activity_v1');
  return !flag.loading && flag.enabled ? <HistoryWorkspace key={loanId} loanId={loanId} /> : null;
}
